// @ts-nocheck

import { useState } from "react";
import { motion } from "framer-motion";
import { useToast } from "@/components/ui/ToastProvider";
import { Badge } from "@/components/ui/Badge";
import { VerifiedBadge } from "@/components/ui/VerifiedBadge";
import { formatCount, formatShortDate } from "@/lib/utils/format";
import { cn } from "@/lib/utils/cn";
import { IconSearch } from "@/components/ui/Icons";
import { Link } from "react-router-dom";

interface AdminTopic {
  id: string; name: string; slug: string; description: string | null;
  color: string; post_count: number; subscriber_count: number;
  is_featured: boolean; is_verified: boolean; is_nsfw: boolean; created_at: string;
}

export function AdminTopicsClient({ topics: initial }: { topics: AdminTopic[] }) {
  const [topics, setTopics] = useState<AdminTopic[]>(initial);
  const [search, setSearch] = useState("");
  const { success, error: showError } = useToast();

  const filtered = topics.filter(t =>
    !search || t.name.toLowerCase().includes(search.toLowerCase()) || t.slug.includes(search.toLowerCase())
  );

  const toggleProp = async (id: string, prop: "is_featured" | "is_verified" | "is_nsfw", current: boolean) => {
    try {
      const res = await fetch(`/api/admin/topics/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ [prop]: !current }),
      });
      if (!res.ok) throw new Error();
      setTopics(prev => prev.map(t => t.id === id ? { ...t, [prop]: !current } : t));
      success("Topic updated");
    } catch {
      showError("Update failed");
    }
  };

  return (
    <div className="space-y-4">
      <div className="relative max-w-md">
        <IconSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-subtle pointer-events-none" />
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search topics..." className="input pl-9 w-full" />
      </div>

      <p className="text-xs text-text-muted">{filtered.length} topics</p>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-surface-overlay">
                <th className="text-left px-4 py-3 text-xs font-semibold text-text-muted">Topic</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-text-muted">Members</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-text-muted">Posts</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-text-muted">Created</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-text-muted">Flags</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-text-muted">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((topic, i) => (
                <motion.tr key={topic.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.02 }}
                  className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${topic.color}20` }}>
                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: topic.color }} />
                      </div>
                      <div>
                        <div className="flex items-center gap-1">
                          <Link to={`/topics/${topic.slug}`} className="text-sm font-medium text-foreground hover:text-brand transition-colors">
                            {topic.name}
                          </Link>
                          {topic.is_verified && <VerifiedBadge size="xs" />}
                        </div>
                        <p className="text-xs text-text-subtle">{topic.slug}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-text-muted tabular-nums">{formatCount(topic.subscriber_count)}</td>
                  <td className="px-4 py-3 text-sm text-text-muted tabular-nums">{formatCount(topic.post_count)}</td>
                  <td className="px-4 py-3 text-xs text-text-subtle">{formatShortDate(topic.created_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      {topic.is_featured && <Badge variant="brand" size="sm">Featured</Badge>}
                      {topic.is_nsfw && <Badge variant="destructive" size="sm">NSFW</Badge>}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1.5">
                      <button onClick={() => toggleProp(topic.id, "is_featured", topic.is_featured)}
                        className={cn("btn-sm text-xs", topic.is_featured ? "btn-secondary" : "btn-ghost")}>
                        {topic.is_featured ? "Unfeature" : "Feature"}
                      </button>
                      <button onClick={() => toggleProp(topic.id, "is_verified", topic.is_verified)}
                        className={cn("btn-sm text-xs", topic.is_verified ? "btn-secondary" : "btn-ghost")}>
                        {topic.is_verified ? "Unverify" : "Verify"}
                      </button>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
